import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { EMPTY, Observable, of, switchMap } from 'rxjs';
import { UserProfile } from 'src/app/authentication/profile/models/user-profile';
import { AuthenticationService } from 'src/app/authentication/services/authentication.service';
import { UserService } from 'src/app/authentication/services/user.service';
import {
    InputFormDialogComponent,
    InputFormDialogData,
    InputFormDialogFieldType,
} from 'src/app/shared/components/input-form-modal/input-form-dialog.component';
import { SubscriptionType } from 'src/app/shared/models/credit-service-dtos';
import { CreditsService } from 'src/app/shared/services/credits.service';
import { ToastMessageService } from 'src/app/shared/services/toast-message.service';

@Component({
    selector: 'app-prepaid-subscription',
    imports: [CommonModule, TranslateModule],
    templateUrl: './prepaid-subscription.component.html',
    styleUrl: '../../../styles.scss',
})
export class PrepaidSubscriptionComponent implements OnInit {
    showLoadingSpinner: boolean;

    urlToDtoSubscriptionConfigMap: {
        [key: string]: { key: string; isOrganization: boolean };
    } = {
        'education-single-pay': {
            key: 'EDUCATION_SINGLE_PAY',
            isOrganization: false,
        },
        'pib-advanced-single-pay': {
            key: 'PIB_ADVANCED_SINGLE_PAY',
            isOrganization: true,
        },
    };

    constructor(
        private creditsService: CreditsService,
        private route: ActivatedRoute,
        private router: Router,
        private toastMessageService: ToastMessageService,
        private authenticationService: AuthenticationService,
        private userService: UserService,
        private dialog: MatDialog,
        private translateService: TranslateService
    ) {}

    ngOnInit(): void {
        const urlSubscriptionType =
            this.route.snapshot.params['subscriptionType'];
        const promotionCode = this.route.snapshot.queryParams['promotionCode'];
        const subscriptionConfig =
            this.urlToDtoSubscriptionConfigMap[urlSubscriptionType];
        this.showLoadingSpinner = true;
        const dto: { priceKey: string; promotionCode?: string } = {
            priceKey: subscriptionConfig.key,
        };
        if (promotionCode) {
            dto['promotionCode'] = promotionCode;
        }

        let contextEnforcement: Observable<any> = of(null);
        if (subscriptionConfig.isOrganization) {
            const data: InputFormDialogData = {
                headerTranslationKey:
                    'xtra.prepaidSubscription.organizationSelection.header',
                submitButtonTranslationKey:
                    'xtra.prepaidSubscription.organizationSelection.submit',
                defaultErrorTranslationKey: 'default.errorMessage',
                submitAction: (userProfile: any) =>
                    this.userService.updateUser(
                        userProfile,
                        this.authenticationService.currentUserValue.id
                    ),
                inputErrorTranslationKeyPrefix:
                    'xtra.prepaidSubscription.errorMessages',
                fields: [
                    {
                        type: InputFormDialogFieldType.SELECT,
                        controlName: 'activeOrganization',
                        labelTranslationKey:
                            'xtra.prepaidSubscription.organizationSelection.label',
                        validators: [
                            Validators.required,
                            Validators.minLength(1),
                        ],
                        infoTranslationKey:
                            'xtra.prepaidSubscription.organizationSelection.infoText',
                        initialValue: '',
                        options: [
                            {
                                name: this.translateService.instant(
                                    'xtra.prepaidSubscription.organizationSelection.label'
                                ),
                                value: '',
                            },
                            ...this.authenticationService.currentUserValue.roles
                                .filter((role) =>
                                    role.endsWith('ADMINISTRATION|SUPERADMIN')
                                )
                                .map((role) => {
                                    const organizationId = role.split('|')[0];
                                    return {
                                        name: this.authenticationService
                                            .currentUserValue
                                            .organizationProperties[
                                            organizationId
                                        ]?.name,
                                        value: organizationId,
                                    };
                                }),
                        ],
                    },
                ],
            };
            contextEnforcement = this.dialog
                .open(InputFormDialogComponent, { data })
                .afterClosed()
                .pipe(
                    switchMap((dialogResult) => {
                        if (
                            dialogResult === undefined ||
                            dialogResult === null
                        ) {
                            this.showLoadingSpinner = false;
                            this.router.navigate(['workspace']);
                            return EMPTY;
                        }
                        return this.authenticationService.refreshUserAuth();
                    })
                );
        } else if (
            this.authenticationService.currentUserValue.activeOrganization !==
            ''
        ) {
            contextEnforcement = this.userService
                .updateUser(
                    {
                        activeOrganization: '',
                    } as UserProfile,
                    this.authenticationService.currentUserValue.id
                )
                .pipe(
                    switchMap(() =>
                        this.authenticationService.refreshUserAuth()
                    )
                );
        }
        contextEnforcement
            .pipe(
                switchMap(() =>
                    this.authenticationService.getCurrentUserSubscription()
                ),
                switchMap((subscriptionDto) => {
                    if (
                        subscriptionDto === null ||
                        [
                            SubscriptionType.FREE,
                            SubscriptionType.UNLIMITED,
                        ].includes(subscriptionDto.subscriptionType)
                    ) {
                        return this.creditsService.createCheckoutSession(dto);
                    } else {
                        throw new Error('Already has subscription.');
                    }
                })
            )
            .subscribe({
                next: (session) => {
                    this.showLoadingSpinner = false;
                    this.navigateTo(session.url);
                },
                error: (error: Error) => {
                    this.showLoadingSpinner = false;
                    let errorKey =
                        'shared.components.subscriptionsPage.components.subscriptions.errors.createCheckoutSession';
                    if (error.message === 'Already has subscription.') {
                        errorKey =
                            'shared.components.subscriptionsPage.components.subscriptions.errors.subscriptionAlreadyExists';
                    }
                    this.toastMessageService.showTranslatedErrorMsg(
                        errorKey,
                        {}
                    );
                    this.router.navigate(['profile']);
                },
            });
    }

    private navigateTo(url: string) {
        window.open(url, '_self');
    }
}
